import { useState, useEffect } from 'react'
import { paymentsApi, type StarBalance, type Transaction } from '../../api/payments'
import { usersApi } from '../../api/users'
import { useUiStore } from '../../stores/uiStore'
import { InvoicesPanel } from './InvoicesPanel'
import type { User } from '../../types'

interface Props {
  onBack: () => void
}

function txIcon(type: Transaction['type']): string {
  switch (type) {
    case 'purchase': return '💳'
    case 'transfer_in': return '⬇️'
    case 'transfer_out': return '⬆️'
    case 'refund': return '↩️'
    default: return '⭐'
  }
}

function txLabel(type: Transaction['type']): string {
  switch (type) {
    case 'purchase': return 'Покупка'
    case 'transfer_in': return 'Получено'
    case 'transfer_out': return 'Отправлено'
    case 'refund': return 'Возврат'
    default: return type
  }
}

export function StarsPanel({ onBack }: Props) {
  const [balance, setBalance] = useState<StarBalance | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [showSend, setShowSend] = useState(false)
  const [showInvoices, setShowInvoices] = useState(false)
  const [sendSearch, setSendSearch] = useState('')
  const [sendResults, setSendResults] = useState<User[]>([])
  const [sendAmount, setSendAmount] = useState('')
  const [sendTarget, setSendTarget] = useState<User | null>(null)
  const [sending, setSending] = useState(false)
  const { addToast } = useUiStore()

  useEffect(() => {
    Promise.all([
      paymentsApi.getBalance(),
      paymentsApi.listTransactions(),
    ])
      .then(([bal, txs]) => {
        setBalance(bal)
        setTransactions(txs.transactions ?? [])
      })
      .catch(() => addToast('error', 'Не удалось загрузить звёзды'))
      .finally(() => setLoading(false))
  }, [addToast])

  const handleSearchUser = async (q: string) => {
    setSendSearch(q)
    if (!q.trim()) { setSendResults([]); return }
    try {
      const r = await usersApi.search(q)
      setSendResults(r.users ?? [])
    } catch { /* ignore */ }
  }

  const handleSend = async () => {
    if (!sendTarget || !sendAmount) return
    const stars = parseInt(sendAmount, 10)
    if (isNaN(stars) || stars <= 0) { addToast('error', 'Неверная сумма'); return }
    setSending(true)
    try {
      await paymentsApi.transferStars(sendTarget.id, stars)
      setBalance((b) => b ? { ...b, stars: b.stars - stars } : b)
      setTransactions((prev) => [
        {
          id: Date.now().toString(),
          user_id: '',
          type: 'transfer_out',
          stars,
          balance_after: (balance?.stars ?? 0) - stars,
          description: `Sent to ${sendTarget.first_name || sendTarget.username}`,
          created_at: new Date().toISOString(),
        },
        ...prev,
      ])
      addToast('success', `Sent ${stars} ⭐ to ${sendTarget.first_name || sendTarget.username}`)
      setShowSend(false)
      setSendTarget(null)
      setSendAmount('')
      setSendSearch('')
    } catch {
      addToast('error', 'Не удалось отправить звёзды')
    } finally {
      setSending(false)
    }
  }

  if (showInvoices) {
    return <InvoicesPanel onBack={() => setShowInvoices(false)} />
  }

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">⭐ Stars</h2>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Balance card */}
            <div className="mx-4 mt-4 bg-gradient-to-br from-yellow-500/20 to-orange-500/10 rounded-2xl p-5 border border-yellow-500/20 text-center">
              <p className="text-5xl font-bold text-txt-primary">{balance?.stars?.toLocaleString() ?? 0}</p>
              <p className="text-txt-meta text-sm mt-1">⭐ Stars balance</p>
              <div className="mt-3 flex gap-2 justify-center">
                <button
                  onClick={() => setShowSend((s) => !s)}
                  className="bg-accent text-white px-5 py-2 rounded-xl text-sm font-medium hover:bg-blue-500 transition-colors"
                >
                  Send Stars
                </button>
                <button
                  onClick={() => setShowInvoices(true)}
                  className="bg-bg-primary text-txt-secondary px-4 py-2 rounded-xl text-sm font-medium hover:bg-bg-hover transition-colors border border-divider"
                >
                  💳 Invoices
                </button>
              </div>
            </div>

            {/* Send Stars form */}
            {showSend && (
              <div className="mx-4 mt-3 bg-bg-input rounded-2xl p-4 space-y-3 border border-divider animate-fade-in">
                <p className="text-txt-primary text-sm font-medium">Send Stars</p>
                {sendTarget ? (
                  <div className="flex items-center gap-2 bg-bg-primary rounded-xl px-3 py-2">
                    <span className="text-txt-primary text-sm flex-1">
                      {sendTarget.first_name || sendTarget.username}
                    </span>
                    <button onClick={() => setSendTarget(null)} className="text-txt-meta hover:text-txt-secondary text-xs">✕</button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      value={sendSearch}
                      onChange={(e) => handleSearchUser(e.target.value)}
                      placeholder="Поиск пользователя…"
                      className="w-full bg-bg-primary rounded-xl px-3 py-2 text-sm text-txt-primary outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
                    />
                    {sendResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-bg-primary border border-divider rounded-xl shadow-xl z-10 max-h-32 overflow-y-auto">
                        {sendResults.slice(0, 5).map((u) => (
                          <button
                            key={u.id}
                            onClick={() => { setSendTarget(u); setSendSearch(''); setSendResults([]) }}
                            className="w-full text-left px-3 py-2 text-txt-primary text-sm hover:bg-bg-hover transition-colors"
                          >
                            {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.username}
                            {u.username && <span className="text-txt-meta text-xs ml-1">@{u.username}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <input
                  type="number"
                  min={1}
                  value={sendAmount}
                  onChange={(e) => setSendAmount(e.target.value)}
                  placeholder="Количество звёзд"
                  className="w-full bg-bg-primary rounded-xl px-3 py-2 text-sm text-txt-primary outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowSend(false); setSendTarget(null); setSendAmount('') }}
                    className="flex-1 bg-bg-primary text-txt-secondary py-2 rounded-xl text-sm hover:bg-bg-hover transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSend}
                    disabled={sending || !sendTarget || !sendAmount}
                    className="flex-1 bg-accent text-white py-2 rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-blue-500 transition-colors"
                  >
                    {sending ? 'Отправка…' : 'Отправить'}
                  </button>
                </div>
              </div>
            )}

            {/* Transaction history */}
            {transactions.length > 0 && (
              <div className="mt-4">
                <p className="text-txt-meta text-xs uppercase tracking-wider font-medium px-4 mb-2">Transaction history</p>
                <div className="space-y-0.5">
                  {transactions.slice(0, 30).map((tx) => (
                    <div key={tx.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-bg-hover transition-colors">
                      <span className="text-xl shrink-0">{txIcon(tx.type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-txt-primary text-sm">{txLabel(tx.type)}</p>
                        {tx.description && (
                          <p className="text-txt-meta text-xs truncate">{tx.description}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`text-sm font-medium ${tx.type === 'transfer_out' ? 'text-red-400' : 'text-green-400'}`}>
                          {tx.type === 'transfer_out' ? '-' : '+'}{tx.stars} ⭐
                        </p>
                        <p className="text-txt-meta text-xs">
                          {new Date(tx.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {transactions.length === 0 && !loading && (
              <div className="text-center py-8 px-4">
                <p className="text-4xl mb-2">⭐</p>
                <p className="text-txt-meta text-sm">No transactions yet</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
